import React from "react";
import { Link } from "react-router-dom";
import { MapPin, CheckCircle2, ArrowRight, Sparkles } from "lucide-react";

// Dashboard "Find a Cleaner" awareness card (Phase 6). Purely presentational:
// reads existing user fields (marketplace_enabled, has_full_access) — no new
// API calls, no dismiss-state storage.
export const MarketplaceCard = ({ user, t }) => {
  const enabled = user?.marketplace_enabled === true;
  const eligible = user?.has_full_access === true;

  if (eligible && !enabled) {
    return (
      <div
        className="order-3 mt-4 rounded-2xl bg-green-50/70 border border-green-200 p-6 sm:p-7"
        data-testid="marketplace-awareness-card"
      >
        <div className="flex flex-col sm:flex-row sm:items-center gap-5">
          <span className="w-11 h-11 rounded-xl bg-white border border-green-200 flex items-center justify-center shrink-0" aria-hidden="true">
            <MapPin className="w-5 h-5 text-green-700" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="inline-flex items-center gap-1.5 text-[11px] font-bold tracking-wide uppercase text-green-700" data-testid="mk-new-badge">
              <Sparkles className="w-3.5 h-3.5" aria-hidden="true" />
              {t("dash.mkNewBadge")}
            </p>
            <h3 className="mt-1.5 text-lg sm:text-xl font-bold text-gray-900 font-display">{t("dash.mkInviteTitle")}</h3>
            <p className="mt-1.5 text-sm text-gray-600 leading-relaxed">{t("dash.mkInviteBody")}</p>
          </div>
          <div className="shrink-0 flex flex-col items-stretch sm:items-center gap-2">
            <Link
              to="/settings#find-a-cleaner"
              className="inline-flex items-center justify-center gap-1.5 h-11 px-6 rounded-md bg-green-700 hover:bg-green-800 text-white text-sm font-semibold shadow-sm transition-colors w-full sm:w-auto"
              data-testid="mk-enable-listing-btn"
            >
              {t("dash.mkEnableCta")} <ArrowRight className="w-4 h-4" />
            </Link>
            <p className="text-xs text-green-800/80 text-center">{t("dash.mkFreeNote")}</p>
          </div>
        </div>
      </div>
    );
  }

  if (eligible && enabled) {
    return (
      <div
        className="order-3 mt-4 bg-white border border-gray-100 shadow-sm rounded-2xl p-6 flex flex-col sm:flex-row sm:items-center gap-4"
        data-testid="marketplace-awareness-card"
      >
        <span className="w-10 h-10 rounded-lg bg-green-50 border border-green-100 flex items-center justify-center shrink-0" aria-hidden="true">
          <MapPin className="w-5 h-5 text-green-800" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-gray-900 flex items-center gap-1.5">
            <CheckCircle2 className="w-4 h-4 text-green-700" /> {t("dash.mkListedTitle")}
          </p>
          <p className="mt-1 text-xs text-gray-600 leading-relaxed">{t("dash.mkListedBody")}</p>
        </div>
        <div className="shrink-0 flex flex-col sm:flex-row gap-2">
          <Link
            to="/find-a-cleaner"
            className="inline-flex items-center justify-center h-10 px-4 rounded-md border border-gray-200 bg-white hover:bg-gray-50 text-sm font-semibold text-gray-800 transition-colors"
            data-testid="mk-view-marketplace-btn"
          >
            {t("dash.mkViewCta")}
          </Link>
          <Link
            to="/settings#find-a-cleaner"
            className="inline-flex items-center justify-center h-10 px-4 rounded-md border border-gray-200 bg-white hover:bg-gray-50 text-sm font-semibold text-gray-800 transition-colors"
            data-testid="mk-manage-listing-btn"
          >
            {t("dash.mkManageCta")}
          </Link>
        </div>
      </div>
    );
  }

  // Expired subscription/trial — never claim public visibility. Styled to sit
  // consistently alongside the amber trial-expired upgrade banner.
  return (
    <div
      className="order-3 mt-4 rounded-2xl bg-amber-50/60 border border-amber-200 p-6 flex flex-col sm:flex-row sm:items-center gap-4"
      data-testid="marketplace-awareness-card"
    >
      <span className="w-10 h-10 rounded-lg bg-white border border-amber-200 flex items-center justify-center shrink-0" aria-hidden="true">
        <MapPin className="w-5 h-5 text-amber-700" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-amber-900">{t("dash.mkInactiveTitle")}</p>
        <p className="mt-1 text-xs text-amber-800/80 leading-relaxed">{t("dash.mkInactiveBody")}</p>
      </div>
      <Link
        to="/settings#find-a-cleaner"
        className="shrink-0 inline-flex items-center justify-center h-10 px-4 rounded-md border border-amber-200 bg-white hover:bg-amber-100/60 text-sm font-semibold text-amber-900 transition-colors"
        data-testid="mk-manage-listing-btn"
      >
        {t("dash.mkManageCta")}
      </Link>
    </div>
  );
};
